import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, UserCog, Plus, Pencil, Trash2, ShieldCheck } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { describeApiError } from '@/lib/permission-errors';
import { toast } from '@/stores/toast';
import { ConfirmDialog } from '@/components/Modal';
import { useLabel } from '@/lib/labels';

/**
 * v0.3 (S-16) ロール一覧・作成・編集・削除画面。
 * 設計: docs/design/v03-custom-roles.md §4.2
 */

type Role = {
  role_id: string;
  tenant_id: string | null;
  role_name: string;
  description: string | null;
  is_system_role: boolean;
  is_system_reserved: boolean;
  assigned_user_count: number;
  updated_at: string;
};

const systemRoleLabelMap: Record<string, string> = {
  platform_admin: 'プラットフォーム管理者',
  company_admin: '会社管理者',
  manager: 'マネージャー',
};

export function RolesSettingsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: roles = [], isLoading } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => apiFetch<Role[]>('/roles'),
  });

  const [creating, setCreating] = useState<{ role_name: string; description: string } | null>(null);
  const [editing, setEditing] = useState<{ role_id: string; role_name: string; description: string } | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  const createMut = useMutation({
    mutationFn: (f: { role_name: string; description: string }) => apiFetch('/roles', {
      method: 'POST', body: JSON.stringify({ role_name: f.role_name, description: f.description || null }),
    }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      setCreating(null);
      toast.success('ロールを作成しました。続けて権限を設定してください');
      if (res?.role_id) window.location.assign(`/settings/roles/${res.role_id}/permissions`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? describeApiError(e) : '作成に失敗しました'),
  });

  const updateMut = useMutation({
    mutationFn: (f: { role_id: string; role_name: string; description: string }) => apiFetch(`/roles/${f.role_id}`, {
      method: 'PATCH', body: JSON.stringify({ role_name: f.role_name, description: f.description || null }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      setEditing(null);
      toast.success('ロールを更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? describeApiError(e) : '更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (roleId: string) => apiFetch(`/roles/${roleId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      const name = deleting?.role_name ?? '';
      setDeleting(null);
      toast.success(`ロール「${name}」を削除しました`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? describeApiError(e) : '削除に失敗しました'),
  });

  const roleLabel = (r: Role) => (r.is_system_role ? systemRoleLabelMap[r.role_name] ?? r.role_name : r.role_name);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-h2">{t('entity.roles', 'ロール管理')}</h2>
          <p className="text-sm text-neutral-sub mt-1">
            テナント独自のカスタムロールを作成し、機能ごとの権限を設定します。システム標準ロール (プラットフォーム管理者/会社管理者/マネージャー) は編集・削除できません
          </p>
        </div>
        <button
          onClick={() => setCreating({ role_name: '', description: '' })}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover"
        >
          <Plus size={16} /> 新規ロール
        </button>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th className="text-left px-4 py-2">{t('field.roles.role_name', 'ロール名')}</th>
                <th className="text-left px-4 py-2 w-28">種別</th>
                <th className="text-right px-4 py-2 w-28">割当ユーザー数</th>
                <th className="text-left px-4 py-2 w-36">更新日</th>
                <th className="text-right px-4 py-2 w-40">操作</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.role_id} className={`border-t border-neutral-line ${r.is_system_reserved ? 'bg-neutral-panel/30' : ''}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{roleLabel(r)}</div>
                    {r.description && <div className="text-xs text-neutral-sub">{r.description}</div>}
                  </td>
                  <td className="px-4 py-2">
                    {r.is_system_reserved ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-neutral-line text-neutral-text">
                        <Lock size={11} /> システム
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white border border-neutral-line text-neutral-text">
                        <UserCog size={11} /> カスタム
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.assigned_user_count}</td>
                  <td className="px-4 py-2 text-neutral-sub text-xs tabular-nums">{new Date(r.updated_at).toLocaleDateString('ja-JP')}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {r.is_system_reserved ? (
                      <Link to={`/settings/roles/${r.role_id}/permissions`} className="text-xs text-brand-red hover:underline inline-flex items-center gap-1">
                        <ShieldCheck size={12} /> 権限を見る
                      </Link>
                    ) : (
                      <>
                        <Link to={`/settings/roles/${r.role_id}/permissions`} title="権限編集"
                          className="text-brand-red p-1 hover:text-brand-red-hover inline-block">
                          <ShieldCheck size={14} />
                        </Link>
                        <button type="button" title="ロール名編集" aria-label="ロール名編集"
                          onClick={() => setEditing({ role_id: r.role_id, role_name: r.role_name, description: r.description ?? '' })}
                          className="ml-1 text-brand-red p-1 hover:text-brand-red-hover">
                          <Pencil size={14} />
                        </button>
                        <button type="button"
                          aria-label={r.assigned_user_count > 0 ? `割当中のユーザーがいます (${r.assigned_user_count}名)` : '削除'}
                          title={r.assigned_user_count > 0 ? `割当中のユーザーがいます (${r.assigned_user_count}名)` : '削除'}
                          disabled={r.assigned_user_count > 0}
                          onClick={() => setDeleting(r)}
                          className="ml-1 p-1 text-neutral-sub hover:text-warning-text disabled:opacity-30 disabled:cursor-not-allowed">
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-neutral-sub">ロールがありません</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 新規作成モーダル */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCreating(null)}>
          <form onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); createMut.mutate(creating); }}
            className="bg-white rounded-lg p-6 w-[480px] max-w-full space-y-3">
            <h3 className="text-h3">新規ロール作成</h3>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.roles.role_name', 'ロール名')}*</label>
              <input required maxLength={100} value={creating.role_name}
                onChange={(e) => setCreating({ ...creating, role_name: e.target.value })}
                placeholder="例：店長"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.roles.description', '説明')}（任意）</label>
              <input maxLength={500} value={creating.description}
                onChange={(e) => setCreating({ ...creating, description: e.target.value })}
                placeholder="例：自店舗のメニューのみ編集可能"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <p className="text-xs text-neutral-sub">作成後、権限マトリクス編集画面に自動的に移動します</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setCreating(null)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button type="submit" disabled={createMut.isPending || !creating.role_name.trim()}
                className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
                {createMut.isPending ? '作成中…' : '作成'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 編集モーダル */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); updateMut.mutate(editing); }}
            className="bg-white rounded-lg p-6 w-[480px] max-w-full space-y-3">
            <h3 className="text-h3">ロール編集</h3>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.roles.role_name', 'ロール名')}*</label>
              <input required maxLength={100} value={editing.role_name}
                onChange={(e) => setEditing({ ...editing, role_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.roles.description', '説明')}（任意）</label>
              <input maxLength={500} value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button type="submit" disabled={updateMut.isPending || !editing.role_name.trim()}
                className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
                {updateMut.isPending ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 削除確認 */}
      <ConfirmDialog
        open={deleting !== null}
        title="ロールを削除しますか？"
        destructive
        confirmLabel="削除する"
        cancelLabel="キャンセル"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.role_id); }}
        message={deleting && (
          <p>
            ロール「<strong>{deleting.role_name}</strong>」を削除します。この操作は<strong className="text-danger-text">取り消せません</strong>
            （監査ログには履歴が残ります）。
          </p>
        )}
      />
    </div>
  );
}
